import React, { useCallback, useEffect, useMemo, useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router";

import type { DetectedService } from "src/domains/application/pages/StackEditPage/types";
import { Application } from "src/domains/application/types";
import { ORGANIZATIONS } from "src/platform/constants/routes";
import { useNotification } from "src/shell/contexts/notificationContext";
import { sectionLabelSx } from "src/shell/styles/themeUtils";

import {
  listSecrets,
  upsertSecret,
  revealSecret,
  deleteSecret,
  listAppResources,
  getEffectiveSecrets,
  type SecretMetadata,
  type ApplicationResourceRecord,
  type EffectiveSecret,
} from "../../../module/api/secretsApi";

// ── Types ─────────────────────────────────────────────────────────────────────

interface SecretsTabProps {
  system: Application;
  onRedeploy?: () => void;
}

// ── Shared layout ─────────────────────────────────────────────────────────────

function CardBox({
  children,
  sx,
}: {
  children: React.ReactNode;
  sx?: object;
}): React.ReactElement {
  return (
    <Box
      sx={{
        border: (t) => `1px solid ${t.palette.divider}`,
        borderRadius: "12px",
        bgcolor: (t) => t.palette.background.paper,
        overflow: "hidden",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function TableHeader({
  columns,
}: {
  columns: { label: string; width?: string }[];
}): React.ReactElement {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        px: 2,
        py: 1.25,
        borderBottom: (t) => `1px solid ${t.palette.divider}`,
        bgcolor: (t) =>
          t.palette.mode === "dark"
            ? "rgba(255,255,255,0.02)"
            : "rgba(0,0,0,0.02)",
      }}
    >
      {columns.map((col) => (
        <Typography
          key={col.label}
          sx={{
            fontSize: "0.6875rem",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: "text.secondary",
            flex: col.width ? undefined : 1,
            width: col.width,
            minWidth: col.width,
          }}
        >
          {col.label}
        </Typography>
      ))}
    </Box>
  );
}

function TableRow({
  children,
  last,
}: {
  children: React.ReactNode;
  last?: boolean;
}): React.ReactElement {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        px: 2,
        py: 1.5,
        borderBottom: last ? "none" : (t) => `1px solid ${t.palette.divider}`,
        alignItems: "center",
        "&:hover": {
          bgcolor: (t) =>
            t.palette.mode === "dark"
              ? "rgba(255,255,255,0.02)"
              : "rgba(0,0,0,0.01)",
        },
      }}
    >
      {children}
    </Box>
  );
}

// ── Skeleton rows ─────────────────────────────────────────────────────────────

function SkeletonTableRows({
  columns,
  rows = 3,
}: {
  columns: number;
  rows?: number;
}): React.ReactElement {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            gap: 2,
            px: 2,
            py: 1.5,
            borderBottom:
              i < rows - 1 ? (t) => `1px solid ${t.palette.divider}` : "none",
          }}
        >
          {Array.from({ length: columns }).map((_, j) => (
            <Skeleton
              key={j}
              variant="text"
              width={j === 0 ? "25%" : j === 1 ? "30%" : "15%"}
              height={16}
            />
          ))}
        </Box>
      ))}
    </>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function SecretsTab({
  system,
  onRedeploy,
}: SecretsTabProps): React.ReactElement {
  return (
    <Stack spacing={2.5}>
      <InheritedSecretsSection system={system} />
      {/* Override info banner */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 2,
          py: 1.5,
          border: (t) => `1px solid ${t.palette.divider}`,
          borderLeft: "3px solid",
          borderLeftColor: "primary.main",
          borderRadius: "8px",
          bgcolor: (t) =>
            t.palette.mode === "dark"
              ? "rgba(59,130,246,0.04)"
              : "rgba(59,130,246,0.02)",
        }}
      >
        <LockIcon sx={{ fontSize: 16, color: "primary.main" }} />
        <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
          Stack secrets override <strong>project</strong> and{" "}
          <strong>org</strong> secrets with the same key.
        </Typography>
      </Box>
      <StackSecretsSection system={system} onRedeploy={onRedeploy} />
      <ResourceSecretsSection system={system} />
    </Stack>
  );
}

// ── Inherited Secrets Section (read-only) ────────────────────────────────────

function InheritedSecretsSection({
  system,
}: {
  system: Application;
}): React.ReactElement | null {
  const [secrets, setSecrets] = useState<EffectiveSecret[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSecrets = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getEffectiveSecrets(system.alias);
      // Filter to only inherited (non-stack) secrets
      setSecrets(data.filter((s) => s.sourceScope !== "stack"));
    } catch {
      setSecrets([]);
    } finally {
      setLoading(false);
    }
  }, [system.alias]);

  useEffect(() => {
    void fetchSecrets();
  }, [fetchSecrets]);

  // Don't render if no inherited secrets and not loading
  if (!loading && secrets.length === 0) return null;

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle2" sx={sectionLabelSx}>
            Inherited secrets
          </Typography>
          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
            read-only — managed at parent scope
          </Typography>
        </Stack>
      </Stack>

      <CardBox>
        <TableHeader
          columns={[
            { label: "Key", width: "30%" },
            { label: "Value", width: "30%" },
            { label: "Scope", width: "80px" },
            { label: "Overridden at stack?", width: "150px" },
            { label: "", width: "120px" },
          ]}
        />
        {loading ? (
          <SkeletonTableRows columns={5} rows={2} />
        ) : (
          secrets.map((secret, i) => (
            <TableRow key={secret.key} last={i === secrets.length - 1}>
              <Box
                component="code"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  width: "30%",
                  minWidth: "30%",
                }}
              >
                {secret.key}
              </Box>
              <Typography
                sx={{
                  fontSize: "0.8125rem",
                  color: "text.secondary",
                  width: "30%",
                  minWidth: "30%",
                }}
              >
                ••••••••••••••••
              </Typography>
              <Box sx={{ width: "80px", minWidth: "80px" }}>
                <Chip
                  label={secret.sourceScope}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: 11 }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: "0.8125rem",
                  color: "text.secondary",
                  width: "150px",
                  minWidth: "150px",
                }}
              >
                {secret.overriddenBy ? secret.overriddenBy : "—"}
              </Typography>
              <Box sx={{ width: "120px", minWidth: "120px" }}>
                {secret.sourceScope === "org" && system.organizationAlias ? (
                  <Button
                    size="small"
                    component={RouterLink}
                    to={ORGANIZATIONS.DETAILS.getPath(system.organizationAlias)}
                    sx={{ textTransform: "none", fontSize: 12 }}
                  >
                    Manage at org →
                  </Button>
                ) : secret.sourceScope === "project" ? (
                  <Button
                    size="small"
                    sx={{ textTransform: "none", fontSize: 12 }}
                  >
                    Manage at project →
                  </Button>
                ) : null}
              </Box>
            </TableRow>
          ))
        )}
      </CardBox>
    </Box>
  );
}

// ── Stack Secrets Section (CRUD) ──────────────────────────────────────────────

function StackSecretsSection({
  system,
  onRedeploy,
}: SecretsTabProps): React.ReactElement {
  const { showNotification } = useNotification();
  const [secrets, setSecrets] = useState<SecretMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [revealLoading, setRevealLoading] = useState<Set<string>>(new Set());
  const [showRedeployPrompt, setShowRedeployPrompt] = useState(false);
  const [redeploying, setRedeploying] = useState(false);

  // Dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activeKey, setActiveKey] = useState<string>("");
  const [formData, setFormData] = useState({ key: "", value: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetchSecrets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listSecrets(system.alias);
      setSecrets(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load secrets");
      setSecrets([]);
    } finally {
      setLoading(false);
    }
  }, [system.alias]);

  useEffect(() => {
    void fetchSecrets();
  }, [fetchSecrets]);

  const requiredEnvVars = useMemo(() => {
    const services = system.stackConfig?._detectedServices as
      DetectedService[] | undefined;
    if (!Array.isArray(services)) return [];
    return services
      .flatMap((s) => s.env ?? [])
      .map((e) => e.name)
      .filter((v, i, arr) => arr.indexOf(v) === i);
  }, [system.stackConfig]);

  const isVaultUnreachable =
    error !== null && error.includes("Vault is unreachable");

  const handleReveal = async (key: string) => {
    if (revealed[key]) {
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }
    setRevealLoading((prev) => new Set(prev).add(key));
    try {
      const value = await revealSecret(system.alias, key);
      setRevealed((prev) => ({ ...prev, [key]: value }));
    } catch (err) {
      showNotification(
        `Failed to reveal secret: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setRevealLoading((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleAdd = () => {
    setFormData({ key: "", value: "" });
    setAddDialogOpen(true);
  };

  const handleEdit = (secret: SecretMetadata) => {
    setActiveKey(secret.key);
    setFormData({ key: secret.key, value: "" });
    setEditDialogOpen(true);
  };

  const handleDelete = (key: string) => {
    setActiveKey(key);
    setDeleteDialogOpen(true);
  };

  const submitAdd = async () => {
    if (!formData.key.trim() || !formData.value.trim()) return;
    setSubmitting(true);
    try {
      await upsertSecret(system.alias, formData.key.trim(), formData.value);
      showNotification("Secret added successfully", "success");
      setAddDialogOpen(false);
      void fetchSecrets();
      setShowRedeployPrompt(true);
    } catch (err) {
      showNotification(
        `Failed to add secret: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitEdit = async () => {
    if (!formData.value.trim()) return;
    setSubmitting(true);
    try {
      await upsertSecret(system.alias, activeKey, formData.value);
      showNotification("Secret updated successfully", "success");
      setEditDialogOpen(false);
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[activeKey];
        return next;
      });
      void fetchSecrets();
      setShowRedeployPrompt(true);
    } catch (err) {
      showNotification(
        `Failed to update secret: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitDelete = async () => {
    setSubmitting(true);
    try {
      await deleteSecret(system.alias, activeKey);
      showNotification("Secret deleted successfully", "success");
      setDeleteDialogOpen(false);
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[activeKey];
        return next;
      });
      void fetchSecrets();
    } catch (err) {
      showNotification(
        `Failed to delete secret: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRedeploy = async () => {
    setShowRedeployPrompt(false);
    if (onRedeploy) {
      setRedeploying(true);
      try {
        await onRedeploy();
      } finally {
        setRedeploying(false);
      }
    }
  };

  return (
    <Box>
      {/* Section header with Add button */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 700 }}>
            Stack secrets
          </Typography>
          <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
            — specific to this stack
          </Typography>
        </Stack>
        <Button
          variant="contained"
          size="small"
          onClick={handleAdd}
          sx={{ textTransform: "none", borderRadius: "8px" }}
        >
          + Add secret
        </Button>
      </Stack>

      {/* Vault unreachable warning (non-blocking) */}
      {isVaultUnreachable && (
        <Alert
          severity="warning"
          icon={false}
          sx={{
            mb: 1.5,
            borderRadius: "8px",
            fontSize: "0.8125rem",
            "& .MuiAlert-message": { py: 0.5 },
          }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => void fetchSecrets()}
              sx={{ textTransform: "none" }}
            >
              Try again
            </Button>
          }
        >
          Can&apos;t reach the secret store (Vault). Secrets may not be
          available. Click retry to try again.
        </Alert>
      )}

      {/* Non-Vault error */}
      {error && !isVaultUnreachable && (
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: "8px" }}>
          {error}
        </Alert>
      )}

      <CardBox>
        <TableHeader
          columns={[
            { label: "Key", width: "30%" },
            { label: "Value", width: "30%" },
            { label: "Last updated", width: "100px" },
            { label: "", width: "140px" },
          ]}
        />
        {loading ? (
          <SkeletonTableRows columns={4} rows={3} />
        ) : secrets.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
              No secrets configured. Click &quot;Add Secret&quot; to add your
              first secret.
            </Typography>
            {requiredEnvVars.length > 0 && (
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                justifyContent="center"
                sx={{ mt: 1.5 }}
              >
                {requiredEnvVars.map((name) => (
                  <Chip
                    key={name}
                    label={name}
                    size="small"
                    variant="outlined"
                    sx={{ fontFamily: "monospace" }}
                  />
                ))}
              </Stack>
            )}
          </Box>
        ) : (
          secrets.map((secret, i) => (
            <TableRow key={secret.key} last={i === secrets.length - 1}>
              <Box
                component="code"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  width: "30%",
                  minWidth: "30%",
                }}
              >
                {secret.key}
              </Box>
              <Typography
                sx={{
                  fontSize: "0.8125rem",
                  fontFamily: "monospace",
                  color: "text.secondary",
                  width: "30%",
                  minWidth: "30%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {revealed[secret.key] ?? "••••••••••••••••"}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  width: "100px",
                  minWidth: "100px",
                }}
              >
                {new Date(secret.updatedAt).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                })}
              </Typography>
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ width: "140px", minWidth: "140px" }}
              >
                <Tooltip title={revealed[secret.key] ? "Hide" : "Reveal"}>
                  <IconButton
                    size="small"
                    onClick={() => handleReveal(secret.key)}
                    disabled={revealLoading.has(secret.key)}
                  >
                    {revealLoading.has(secret.key) ? (
                      <CircularProgress size={16} />
                    ) : revealed[secret.key] ? (
                      <VisibilityOffIcon fontSize="small" />
                    ) : (
                      <VisibilityIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => handleEdit(secret)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(secret.key)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </TableRow>
          ))
        )}
      </CardBox>

      {showRedeployPrompt && onRedeploy && (
        <Alert
          severity="info"
          sx={{ mt: 2, borderRadius: "8px" }}
          onClose={() => setShowRedeployPrompt(false)}
          action={
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={() => void handleRedeploy()}
              disabled={redeploying}
              sx={{ textTransform: "none" }}
            >
              {redeploying ? <CircularProgress size={16} /> : "Redeploy now"}
            </Button>
          }
        >
          Secret saved. Redeploy to apply changes to your running stack.
        </Alert>
      )}

      {/* Add Secret Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Secret</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Secret Key"
            placeholder="STRIPE_API_KEY"
            value={formData.key}
            onChange={(e) =>
              setFormData((p) => ({ ...p, key: e.target.value }))
            }
            fullWidth
            inputProps={{ pattern: "^[A-Z][A-Z0-9_]*$" }}
            helperText="UPPER_SNAKE_CASE. This is the env var name your app will receive."
          />
          <TextField
            margin="dense"
            label="Secret Value"
            placeholder="sk_test_..."
            value={formData.value}
            onChange={(e) =>
              setFormData((p) => ({ ...p, value: e.target.value }))
            }
            fullWidth
            multiline
            rows={3}
            helperText="Encrypted at rest in Vault. Never shown in list view."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitAdd}
            disabled={
              submitting || !formData.key.trim() || !formData.value.trim()
            }
          >
            {submitting ? <CircularProgress size={20} /> : "Add Secret"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Secret Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Secret</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Secret Key"
            value={activeKey}
            disabled
            fullWidth
          />
          <TextField
            autoFocus
            margin="dense"
            label="New Secret Value"
            value={formData.value}
            onChange={(e) =>
              setFormData((p) => ({ ...p, value: e.target.value }))
            }
            fullWidth
            multiline
            rows={3}
            helperText="Enter the new value. The previous value will be overwritten."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitEdit}
            disabled={submitting || !formData.value.trim()}
          >
            {submitting ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Secret</DialogTitle>
        <DialogContent>
          <Typography>
            Delete secret{" "}
            <strong style={{ fontFamily: "monospace" }}>{activeKey}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. The secret will be removed from Vault
            and from your app&apos;s environment on the next deploy.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={submitDelete}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Resource Secrets Section (read-only, redacted) ────────────────────────────

function ResourceSecretsSection({
  system,
}: {
  system: Application;
}): React.ReactElement | null {
  const [resources, setResources] = useState<ApplicationResourceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const fetchResources = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listAppResources(system.alias);
      setResources(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load resource secrets",
      );
      setResources([]);
    } finally {
      setLoading(false);
    }
  }, [system.alias]);

  useEffect(() => {
    void fetchResources();
  }, [fetchResources]);

  const toggleExpand = (alias: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(alias)) next.delete(alias);
      else next.add(alias);
      return next;
    });
  };

  const getEnvVars = (
    resource: ApplicationResourceRecord,
  ): { name: string; redacted: boolean }[] => {
    const prefix = resource.alias.toUpperCase().replace(/-/g, "_");
    switch (resource.resource.resourceType) {
      case "postgres":
        return [
          { name: `${prefix}_DATABASE_URL`, redacted: true },
          { name: `${prefix}_DIRECT_URL`, redacted: true },
          { name: `${prefix}_DATABASE_USERNAME`, redacted: true },
          { name: `${prefix}_DATABASE_PASSWORD`, redacted: true },
          { name: `${prefix}_DATABASE_NAME`, redacted: true },
        ];
      case "nats":
        return [
          { name: `${prefix}_NATS_URL`, redacted: false },
          { name: `${prefix}_NATS_CREDS`, redacted: true },
        ];
      case "redis":
        return [{ name: `${prefix}_REDIS_URL`, redacted: false }];
      default:
        return [];
    }
  };

  if (!loading && !error && resources.length === 0) return null;

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" sx={sectionLabelSx}>
          Resource secrets
        </Typography>
        {resources.length > 0 && (
          <Chip label={resources.length} size="small" variant="outlined" />
        )}
      </Stack>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1.5, fontSize: "0.8125rem" }}
      >
        Credentials automatically provisioned by managed resources attached to
        this application. Values are redacted — these are injected at deploy
        time.
      </Typography>

      {loading ? (
        <CardBox>
          <Box sx={{ p: 2 }}>
            {[1, 2].map((i) => (
              <Box key={i} sx={{ display: "flex", gap: 2, py: 1.5 }}>
                <Skeleton variant="text" width="20%" height={16} />
                <Skeleton variant="rounded" width={60} height={20} />
                <Skeleton variant="text" width="30%" height={16} />
              </Box>
            ))}
          </Box>
        </CardBox>
      ) : error ? (
        <Alert severity="error" sx={{ borderRadius: "8px" }}>
          {error}
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          {resources.map((ar) => {
            const envVars = getEnvVars(ar);
            return (
              <CardBox key={ar.id} sx={{ p: 2 }}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography
                        sx={{ fontSize: "0.875rem", fontWeight: 600 }}
                      >
                        {ar.alias}
                      </Typography>
                      <Chip
                        label={ar.resource.resourceType}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ height: 20, fontSize: 11 }}
                      />
                      <Chip
                        label={ar.resource.status}
                        size="small"
                        color={
                          ar.resource.status === "running"
                            ? "success"
                            : "default"
                        }
                        variant="outlined"
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    </Stack>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: "text.secondary",
                        mt: 0.5,
                      }}
                    >
                      {ar.resource.alias}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    onClick={() => toggleExpand(ar.alias)}
                    sx={{ textTransform: "none" }}
                  >
                    {expanded.has(ar.alias) ? "Hide" : "Show"} env vars
                  </Button>
                </Stack>
                <Collapse in={expanded.has(ar.alias)}>
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.5,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                    }}
                  >
                    {envVars.map((envVar) => (
                      <Stack
                        key={envVar.name}
                        direction="row"
                        spacing={2}
                        sx={{ py: 0.25 }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.8125rem",
                            color: "primary.main",
                            fontWeight: 600,
                            minWidth: 280,
                          }}
                        >
                          {envVar.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.8125rem",
                            color: "text.secondary",
                          }}
                        >
                          {envVar.redacted
                            ? "••••••••••••••••"
                            : "(auto-injected)"}
                        </Typography>
                      </Stack>
                    ))}
                  </Box>
                </Collapse>
              </CardBox>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

export default SecretsTab;
